/* eslint-disable @typescript-eslint/no-explicit-any */
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import ListItem from "@mui/material/ListItem";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { useState, useEffect } from "react";
import { camelCaseToHumanReadable } from "../../../helpers/utils";
import { CropTypes } from "../../../types/chemicals";
import { useTranslation } from "react-i18next";
import { SxProps, Theme } from "@mui/material/styles";
import {
  findCropTypeByNameOrAlias,
  ICropType,
} from "../../../types/cropType";
import { useAppContext } from "../../../contexts/useStorageContext";
import { UserRoles } from "../../../types/auth";
import CropTypeFormDialog from "../CropTypes/CropTypeFormDialog";
import { loadCropTypes } from "../../../helpers/loadCropTypes";

const filter = createFilterOptions<ICropType>();
const CREATE_NEW_CROP_TYPE_ID = "new";

interface ISampleMaterialTypeSelectProps {
  label?: string;
  placeholder?: string;
  value?: CropTypes;
  onChange: (newVal?: CropTypes) => void;
  error?: boolean;
  helperText?: string;
  allOptions?: ICropType[];
  focusOnValue?: boolean;
  onOptionsChange?: (options: ICropType[]) => void;
  disabled?: boolean;
  disableClearable?: boolean;
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  onDismiss?: () => void;
}
export default function SampleMaterialTypeSelect(
  props: ISampleMaterialTypeSelectProps
) {
  const { value, onChange, helperText, error, onOptionsChange } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const { state, dispatch } = useAppContext();
  const isSuperAdmin = state.loggedInUserInfo?.role === UserRoles.superadmin;
  const storeCropTypes = state.cropTypes || [];
  const [allowedMaterialTypes, setAllowedMaterialTypes] = useState<ICropType[]>(
    props.allOptions || []
  );
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [prefilledName, setPrefilledName] = useState("");

  const updateOptions = (options: ICropType[]) => {
    setAllowedMaterialTypes(options);
    onOptionsChange?.(options);
  };

  const refreshAllowedMaterialTypes = async () => {
    const fetched = await loadCropTypes(dispatch);
    updateOptions(fetched);
  };

  useEffect(() => {
    if (props.allOptions?.length) {
      updateOptions(props.allOptions);
    } else {
      updateOptions(storeCropTypes);
    }
  }, [props.allOptions, storeCropTypes]);

  const selectedOption =
    (value
      ? (findCropTypeByNameOrAlias(value, allowedMaterialTypes) as
          | ICropType
          | undefined)
      : undefined) || null;

  return (
    <>
      <Autocomplete
        disabled={props.disabled}
        disableClearable={props.disableClearable}
        PopperComponent={(popperProps) => (
          <Popper
            {...popperProps}
            style={{
              ...popperProps.style,
              width: "auto",
              minWidth: popperProps.style?.width,
              zIndex: theme.zIndex.modal + 1,
            }}
            placement="bottom-start"
          />
        )}
        componentsProps={{
          paper: {
            sx: {
              width: "max-content",
              minWidth: "100%",
              maxWidth: "none",
            },
          },
        }}
        ListboxProps={{
          sx: {
            "& .MuiAutocomplete-option": {
              whiteSpace: "nowrap",
            },
          },
        }}
        value={selectedOption}
        openOnFocus={props.autoFocus}
        onClose={(_event, reason) => {
          if (
            reason === "selectOption" ||
            reason === "createOption" ||
            reason === "removeOption"
          ) {
            return;
          }
          props.onDismiss?.();
        }}
        onChange={(_event, newValue) => {
          if (newValue?._id === CREATE_NEW_CROP_TYPE_ID) {
            setPrefilledName((newValue as any).inputValue || "");
            setOpenCreateDialog(true);
          } else {
            onChange(newValue?.name as CropTypes | undefined);
          }
        }}
        options={allowedMaterialTypes}
        getOptionLabel={(option) =>
          camelCaseToHumanReadable(option.name || "")
        }
        isOptionEqualToValue={(option, val) => option.name === val.name}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const input = params.inputValue?.trim().toLowerCase();
          if (input) {
            options.forEach((option) => {
              if (filtered.includes(option)) return;
              const aliasHit = (option.aliases || []).some((alias) =>
                alias.toLowerCase().includes(input)
              );
              if (aliasHit) filtered.push(option);
            });
          }
          if (isSuperAdmin) {
            filtered.push({
              _id: CREATE_NEW_CROP_TYPE_ID,
              name: t("Create New Crop Type"),
              inputValue: params.inputValue,
            } as ICropType & { inputValue?: string });
          }
          return filtered;
        }}
        renderOption={(props, option) => (
          <ListItem {...props} key={option._id || option.name}>
            {option._id === CREATE_NEW_CROP_TYPE_ID ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <AddCircleOutlineIcon color="primary" sx={{ mr: 1 }} />
                {option.name}
                {(option as any).inputValue
                  ? `: "${(option as any).inputValue}"`
                  : ""}
              </div>
            ) : (
              <div>
                {camelCaseToHumanReadable(option.name)}
                {option.aliases?.length ? (
                  <span style={{ opacity: 0.65, marginLeft: 8, fontSize: 12 }}>
                    ({option.aliases.join(", ")})
                  </span>
                ) : null}
              </div>
            )}
          </ListItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label}
            placeholder={props.placeholder || t("Material Type")}
            helperText={helperText}
            error={error}
            size="small"
            fullWidth
            autoFocus={props.autoFocus}
            focused={props.focusOnValue && !!value}
            sx={props.sx}
          />
        )}
      />
      {openCreateDialog && (
        <CropTypeFormDialog
          open
          onClose={() => setOpenCreateDialog(false)}
          initialCropType={
            prefilledName
              ? {
                  _id: "",
                  name: prefilledName,
                  description: "",
                  isPublic: true,
                }
              : undefined
          }
          onSave={async (created) => {
            await refreshAllowedMaterialTypes();
            if (created?.name) {
              onChange(created.name as CropTypes);
            }
          }}
        />
      )}
    </>
  );
}
